import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  GripHorizontal,
  Minus,
  Square,
  X,
  PanelBottom,
  Phone,
  Video,
  Monitor,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Badge } from '../../../components/ui/badge';
import { useTeamChat } from '../store/TeamChatContext';
import { teamService } from '../../../services/team/TeamService';
import { formatEditDeleteWindowHelp } from '../utils/messagePolicy';
import { ChatMessageList } from './ChatMessageList';
import { TeamChatComposer } from './TeamChatComposer';
import { TeamChatMeetingModal } from './TeamChatMeetingModal';
import { useAuth } from '../../../contexts/AuthContext';
import { dmConversationId } from '../utils/conversationId';
import type { FloatingPeerWindow } from '../types';
import { cn } from '../../../components/ui/utils';

function displayNameForPeer(peerId: string): string {
  const m = teamService.getMember(peerId);
  if (!m) return peerId;
  return `${m.firstName} ${m.lastName}`.trim() || m.email;
}

export function TeamChatWindowsPortal() {
  const { floating } = useTeamChat();
  const visible = useMemo(() => floating.filter((w) => w.presentation !== 'docked'), [floating]);

  if (typeof document === 'undefined' || visible.length === 0) return null;

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[10060]">
      <AnimatePresence initial={false}>
        {visible
          .slice()
          .sort((a, b) => a.geom.zIndex - b.geom.zIndex)
          .map((w) => (
            <FloatingChatWindow key={w.peerId} windowState={w} />
          ))}
      </AnimatePresence>
    </div>,
    document.body,
  );
}

function FloatingChatWindow({ windowState }: { windowState: FloatingPeerWindow }) {
  const { peerId, geom } = windowState;
  const {
    closeFloatingChat,
    focusFloating,
    setWindowGeom,
    dockFloatingChat,
    sendDm,
    notifyTyping,
    typingPeerIds,
    getMessages,
    markConversationRead,
    blob,
    editOwnDmMessage,
    softDeleteOwnDmMessage,
    canViewDeletionAudit,
  } = useTeamChat();
  const { user } = useAuth();
  const userId = user?.id || 'me';
  const [draft, setDraft] = useState('');
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [meetingTab, setMeetingTab] = useState<'audio' | 'video' | 'screen'>('audio');
  const dragRef = useRef<{ sx: number; sy: number; ox: number; oy: number; lx: number; ly: number } | null>(null);
  const resizeRef = useRef<{ sx: number; sy: number; ow: number; oh: number } | null>(null);
  const typingTimer = useRef<number | null>(null);

  const messages = useMemo(() => getMessages(peerId), [getMessages, peerId, blob.messagesByConversation]);

  const convId = dmConversationId(userId, peerId);
  const peerReadUpTo = blob.readReceipts[convId]?.[peerId];
  const peerReadIdx = peerReadUpTo ? messages.findIndex((x) => x.id === peerReadUpTo) : -1;

  useEffect(() => {
    markConversationRead(peerId);
  }, [markConversationRead, peerId, messages.length]);

  const onPointerDownHeader = (e: React.PointerEvent) => {
    if (geom.maximized) return;
    focusFloating(peerId);
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    dragRef.current = {
      sx: e.clientX,
      sy: e.clientY,
      ox: geom.x,
      oy: geom.y,
      lx: geom.x,
      ly: geom.y,
    };
  };

  const onPointerMoveHeader = (e: React.PointerEvent) => {
    if (!dragRef.current || geom.maximized) return;
    const margin = 8;
    const dx = e.clientX - dragRef.current.sx;
    const dy = e.clientY - dragRef.current.sy;
    const winW = geom.w;
    const winH = geom.minimized ? 48 : geom.h;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 1080;
    const nx = Math.min(Math.max(margin, dragRef.current.ox + dx), Math.max(margin, vw - winW - margin));
    const ny = Math.min(Math.max(margin, dragRef.current.oy + dy), Math.max(margin, vh - winH - margin));
    dragRef.current.lx = nx;
    dragRef.current.ly = ny;
    setWindowGeom(peerId, { x: nx, y: ny });
  };

  const onPointerUpHeader = (e: React.PointerEvent) => {
    const d = dragRef.current;
    dragRef.current = null;
    try {
      (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
    if (!d || geom.maximized) return;
    const moved = Math.abs(e.clientX - d.sx) + Math.abs(e.clientY - d.sy) > 3;
    if (!moved) return;
    const margin = 8;
    const snap = 14;
    const winW = geom.w;
    const winH = geom.minimized ? 48 : geom.h;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let x = d.lx;
    let y = d.ly;
    if (x <= margin + snap) x = margin;
    else if (x >= vw - winW - margin - snap) x = Math.max(margin, vw - winW - margin);
    if (y <= margin + snap) y = margin;
    else if (y >= vh - winH - margin - snap) y = Math.max(margin, vh - winH - margin);
    x = Math.min(Math.max(margin, x), Math.max(margin, vw - winW - margin));
    y = Math.min(Math.max(margin, y), Math.max(margin, vh - winH - margin));
    setWindowGeom(peerId, { x, y });
  };

  const onPointerDownResize = (e: React.PointerEvent) => {
    if (geom.maximized) return;
    e.stopPropagation();
    focusFloating(peerId);
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    resizeRef.current = { sx: e.clientX, sy: e.clientY, ow: geom.w, oh: geom.h };
  };

  const onPointerMoveResize = (e: React.PointerEvent) => {
    if (!resizeRef.current || geom.maximized) return;
    const margin = 8;
    const dx = e.clientX - resizeRef.current.sx;
    const dy = e.clientY - resizeRef.current.sy;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const maxW = Math.max(280, vw - geom.x - margin);
    const maxH = Math.max(220, vh - geom.y - margin);
    const nw = Math.min(Math.max(280, resizeRef.current.ow + dx), maxW);
    const nh = Math.min(Math.max(220, resizeRef.current.oh + dy), maxH);
    setWindowGeom(peerId, { w: nw, h: nh });
  };

  const onPointerUpResize = (e: React.PointerEvent) => {
    resizeRef.current = null;
    try {
      (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const debouncedTyping = useCallback(() => {
    if (typingTimer.current) window.clearTimeout(typingTimer.current);
    typingTimer.current = window.setTimeout(() => notifyTyping(peerId), 400);
  }, [notifyTyping, peerId]);

  const send = async () => {
    const t = draft.trim();
    if (!t) return;
    setDraft('');
    await sendDm(peerId, t);
  };

  const typing = typingPeerIds.has(peerId);
  const title = displayNameForPeer(peerId);

  const boxStyle: React.CSSProperties = geom.maximized
    ? { left: 16, top: 72, width: 'min(560px, calc(100vw - 32px))', height: 'min(72vh, calc(100dvh - 120px))' }
    : {
        left: geom.x,
        top: geom.y,
        width: geom.w,
        height: geom.minimized ? 48 : geom.h,
      };

  return (
    <>
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.98, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.98, y: 8 }}
        transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{
          ...boxStyle,
          zIndex: geom.zIndex,
        }}
        className={cn(
          'pointer-events-auto absolute flex flex-col overflow-hidden rounded-2xl',
          'border border-card-border/50 bg-card-bg/92 text-foreground shadow-2xl shadow-black/40',
          'backdrop-blur-2xl ring-1 ring-cyber-cyan/15',
        )}
        onMouseDown={() => focusFloating(peerId)}
      >
        <div
          className="flex cursor-grab items-center justify-between gap-2 border-b border-card-border/40 bg-gradient-to-r from-cyber-cyan/10 via-transparent to-cyber-blue/10 px-2 py-1.5 transition-colors duration-300 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] active:cursor-grabbing"
          onPointerDown={onPointerDownHeader}
          onPointerMove={onPointerMoveHeader}
          onPointerUp={onPointerUpHeader}
          onPointerCancel={onPointerUpHeader}
        >
          <div className="flex min-w-0 items-center gap-2">
            <GripHorizontal className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate text-sm font-semibold text-foreground">{title}</span>
            {typing ? (
              <Badge variant="secondary" className="border-cyber-cyan/25 bg-cyber-cyan/10 text-[10px] text-cyber-cyan">
                typing…
              </Badge>
            ) : null}
          </div>
          <div className="flex items-center gap-0.5">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-lg text-muted-foreground transition-colors duration-200 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] hover:bg-cyber-green/15 hover:text-cyber-green active:scale-[0.97]"
              title="Audio"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setMeetingTab('audio');
                setMeetingOpen(true);
              }}
            >
              <Phone className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-lg text-muted-foreground transition-colors duration-200 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] hover:bg-cyber-purple/15 hover:text-cyber-purple active:scale-[0.97]"
              title="Video"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setMeetingTab('video');
                setMeetingOpen(true);
              }}
            >
              <Video className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-lg text-muted-foreground transition-colors duration-200 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] hover:bg-cyber-cyan/15 hover:text-cyber-cyan active:scale-[0.97]"
              title="Screen"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setMeetingTab('screen');
                setMeetingOpen(true);
              }}
            >
              <Monitor className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-lg text-muted-foreground hover:bg-muted/40"
              title="Minimize"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setWindowGeom(peerId, { minimized: !geom.minimized });
              }}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-lg text-muted-foreground hover:bg-cyber-cyan/10 hover:text-cyber-cyan"
              title="Dock to bottom bar"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                dockFloatingChat(peerId);
              }}
            >
              <PanelBottom className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-lg text-muted-foreground hover:bg-muted/40"
              title="Maximize"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setWindowGeom(peerId, { maximized: !geom.maximized, minimized: false });
              }}
            >
              <Square className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-lg hover:bg-rose-500/20"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                closeFloatingChat(peerId);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {!geom.minimized && (
          <>
            <ScrollArea className="min-h-0 flex-1 px-3 py-2">
              <ChatMessageList
                messages={messages}
                userId={userId}
                canViewDeletionAudit={canViewDeletionAudit}
                compact
                peerReadIdx={peerReadIdx}
                onEditOwn={(id, body) => editOwnDmMessage(peerId, id, body)}
                onDeleteOwn={(id) => softDeleteOwnDmMessage(peerId, id)}
              />
            </ScrollArea>

            <div className="space-y-1 border-t border-card-border/40 bg-card-bg/40 p-2">
              <p className="text-[10px] text-muted-foreground px-0.5">{formatEditDeleteWindowHelp()}</p>
              <TeamChatComposer
                compact
                value={draft}
                onChange={setDraft}
                onSend={() => void send()}
                onFiles={async (files) => {
                  await sendDm(peerId, draft.trim(), files);
                  setDraft('');
                }}
                onTyping={debouncedTyping}
                placeholder="Message…"
              />
            </div>

            {!geom.maximized ? (
              <div
                className="pointer-events-auto absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize rounded-tl-lg border-l border-t border-cyber-cyan/25 bg-gradient-to-tl from-cyber-cyan/30 to-transparent"
                title="Resize"
                onPointerDown={onPointerDownResize}
                onPointerMove={onPointerMoveResize}
                onPointerUp={onPointerUpResize}
                onPointerCancel={onPointerUpResize}
              />
            ) : null}
          </>
        )}
      </motion.div>

      <TeamChatMeetingModal
        open={meetingOpen}
        onOpenChange={setMeetingOpen}
        peerName={title}
        initialTab={meetingTab}
      />
    </>
  );
}
